import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import OrderStatusChip from '../../components/OrderStatusChip';
import SchedulingGroupCard from '../../components/SchedulingGroupCard';
import JobCrewCalendar from '../../scheduleCalendar/components/JobCrewCalendar';
import useProjectsData from '../../hooks/useProjectsData';
import {
  WORK_TYPE_LABELS,
  assignmentsForOrder,
  contactForJob,
  departmentLabel,
  formatCurrency,
  formatDateDisplay,
  invoicesForJob,
  invoicedTotalForJob,
  jobById,
  ordersForJob,
  scheduledDateRange,
  scheduledHours,
  statusHistoryForOrder,
} from '../../utils/projectsUtils';

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} component="div" sx={{ textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

const ProjectJobDetailPage: React.FunctionComponent = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const history = useHistory();
  const { loading, error } = useProjectsData(); // subscribe so the page re-renders once live data loads
  const job = jobById(jobId);
  const orders = ordersForJob(jobId);
  // Phase Overview rows sorted by scheduled-window start (ascending); orders with no
  // scheduled window fall to the bottom.
  const phaseOrders = [...orders]
    .map((order) => ({ order, first: scheduledDateRange(assignmentsForOrder(order.order_no)).first }))
    .sort((a, b) => {
      if (a.first && b.first) return a.first.localeCompare(b.first);
      if (a.first) return -1;
      if (b.first) return 1;
      return a.order.order_no.localeCompare(b.order.order_no);
    })
    .map((entry) => entry.order);
  const contact = contactForJob(jobId);
  const invoices = invoicesForJob(jobId);

  const [tab, setTab] = useState<number>(0);
  const [historyOrderNo, setHistoryOrderNo] = useState<string>(orders[0]?.order_no || '');
  const [expandedOrderNo, setExpandedOrderNo] = useState<string | null>(null);

  if (!job) {
    return (
      <Box sx={{ p: 3 }}>
        {loading ? (
          <Typography variant="h6" color="text.secondary">
            Loading project data…
          </Typography>
        ) : (
          <>
            <Typography variant="h6">
              {error ? 'Couldn’t load project data from ServMan.' : `Job ${jobId} not found.`}
            </Typography>
            <Button onClick={() => history.push('/projects/jobs')}>Back to Jobs</Button>
          </>
        )}
      </Box>
    );
  }

  const hoursUsed = job.total_hrs - job.hrs_remain;
  const hoursPercent = job.total_hrs > 0 ? Math.round((hoursUsed / job.total_hrs) * 100) : 0;
  const invoicedTotal = invoicedTotalForJob(jobId);
  const revenueBacklog = job.contract_amount - invoicedTotal;
  const statusChanges = statusHistoryForOrder(historyOrderNo);

  const cityStateZip = [contact?.city, contact?.state].filter(Boolean).join(', ');
  const addressBlock =
    contact?.address1 || cityStateZip
      ? [contact?.address1, contact?.address2, [cityStateZip, contact?.zip].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', ')
      : '—';

  const showHistoryForOrder = (orderNo: string) => {
    setHistoryOrderNo(orderNo);
    setTab(2);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        mainTitle={contact?.company || job.description}
        subTitle={`${job.job_id} · ${job.description}`}
        titleIcon={<CalendarMonthIcon />}
        backButtonLink="/projects/jobs"
        backButtonLabel="All Jobs"
        button={
          <Button
            variant="contained"
            startIcon={<EditCalendarIcon />}
            onClick={() => history.push(`/projects/scheduling?job=${job.job_id}`)}
          >
            Schedule Now
          </Button>
        }
      />

      <Grid container spacing={3} sx={{ mb: 1 }}>
        {/* Left column — job information & financial summary */}
        <Grid item xs={12} md={4} lg={3}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Customer (CLIENT)
          </Typography>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <InfoRow label="Customer" value={contact?.company || '—'} />
              <InfoRow label="Primary Contact" value={contact?.contact_name || '—'} />
              <InfoRow label="Phone" value={contact?.phone || '—'} />
              <InfoRow label="Email" value={contact?.email || '—'} />
              <InfoRow label="Site Address" value={addressBlock} />
              <InfoRow label="Customer Type" value={contact?.client_type || '—'} />
              <InfoRow label="Terms" value={contact?.terms || '—'} />
              <InfoRow label="Account #" value={job.acct_no} />
            </CardContent>
          </Card>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Job Information
          </Typography>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <InfoRow label="Customer PO #" value={contact?.po_no || '—'} />
              <InfoRow label="Department" value={`${departmentLabel(job.dept_id)} (${job.dept_id})`} />
              <InfoRow label="Project Coordinator" value={job.project_coordinator || '—'} />
              <InfoRow label="Start Date" value={formatDateDisplay(job.date_start)} />
              <InfoRow label="Completion Date" value={formatDateDisplay(job.date_complete)} />
            </CardContent>
          </Card>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Financial Summary
          </Typography>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <InfoRow label="Contract Amount" value={formatCurrency(job.contract_amount)} />
              <InfoRow label="Invoiced to Date" value={formatCurrency(invoicedTotal)} />
              <InfoRow
                label="Revenue Backlog"
                value={
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={revenueBacklog > 0 ? 'primary.main' : 'success.main'}
                  >
                    {formatCurrency(revenueBacklog)}
                  </Typography>
                }
              />
              <Divider sx={{ my: 1 }} />
              <InfoRow label="Budgeted Hours" value={job.total_hrs.toLocaleString()} />
              <InfoRow label="Hours Used" value={hoursUsed.toLocaleString()} />
              <InfoRow label="Hours Remaining" value={job.hrs_remain.toLocaleString()} />
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {hoursPercent}% of budgeted hours used
                </Typography>
                <LinearProgress variant="determinate" value={hoursPercent} sx={{ height: 6, borderRadius: 3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right column — phase & financial overview tables */}
        <Grid item xs={12} md={8} lg={9}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Phase Overview
          </Typography>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Phase</TableCell>
                  <TableCell>Order #</TableCell>
                  <TableCell>Scheduled Window</TableCell>
                  <TableCell>Schedule Status</TableCell>
                  <TableCell>Job Status</TableCell>
                  <TableCell>Lead Tech</TableCell>
                  <TableCell align="right">Scheduled Hrs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {phaseOrders.map((order) => {
                  const assignments = assignmentsForOrder(order.order_no);
                  const range = scheduledDateRange(assignments);
                  return (
                    <TableRow key={order.order_no} hover>
                      <TableCell>
                        <Link component="button" underline="hover" onClick={() => showHistoryForOrder(order.order_no)}>
                          {WORK_TYPE_LABELS[order.work_type] || order.work_type}
                        </Link>
                      </TableCell>
                      <TableCell>{order.order_no}</TableCell>
                      <TableCell>
                        {range.first ? `${formatDateDisplay(range.first)} – ${formatDateDisplay(range.last)}` : '—'}
                      </TableCell>
                      <TableCell>
                        {order.unscheduled ? (
                          <Chip label="Needs Scheduled" size="small" color="warning" variant="outlined" />
                        ) : (
                          <Chip
                            label="Scheduled"
                            size="small"
                            sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <OrderStatusChip status={order.status} />
                      </TableCell>
                      <TableCell>{order.lead_tech || '—'}</TableCell>
                      <TableCell align="right">{scheduledHours(assignments).toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Financial Overview
          </Typography>
          <Card variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell>Payment Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box sx={{ py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Not yet invoiced — the full {formatCurrency(job.contract_amount)} contract is still in
                          backlog.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Customer invoices appear here automatically once they’re billed in ServMan.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {invoices.map((invoice) => (
                  <TableRow key={invoice.invoice_no} hover>
                    <TableCell>{invoice.invoice_no}</TableCell>
                    <TableCell>{formatDateDisplay(invoice.date)}</TableCell>
                    <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                    <TableCell align="right">{formatCurrency(invoice.paid_amount)}</TableCell>
                    <TableCell>{invoice.paid_status}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: 2, borderColor: 'divider' } }}>
                  <TableCell>TOTAL INVOICED</TableCell>
                  <TableCell />
                  <TableCell align="right">{formatCurrency(invoicedTotal)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(invoices.reduce((total, invoice) => total + invoice.paid_amount, 0))}
                  </TableCell>
                  <TableCell>
                    Backlog: {formatCurrency(revenueBacklog)} of {formatCurrency(job.contract_amount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>
      </Grid>

      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Scheduling Groups" />
        <Tab label="Crew Schedule" />
        <Tab label="Status History" />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Project Information</Typography>
            <Typography variant="body2" color="text.secondary">
              {orders.length} scheduling groups
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {orders.map((order) => {
              const isExpanded = expandedOrderNo === order.order_no;
              return (
                <Grid item xs={12} sm={isExpanded ? 12 : 6} lg={isExpanded ? 12 : 4} key={order.order_no}>
                  <SchedulingGroupCard
                    order={order}
                    expanded={isExpanded}
                    onToggleExpand={() =>
                      setExpandedOrderNo((prev) => (prev === order.order_no ? null : order.order_no))
                    }
                    onShowHistory={showHistoryForOrder}
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {tab === 1 && <JobCrewCalendar jobId={jobId} />}

      {tab === 2 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Status History</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={historyOrderNo}
              onChange={(_event, value) => value && setHistoryOrderNo(value)}
            >
              {orders.map((order) => (
                <ToggleButton key={order.order_no} value={order.order_no}>
                  {order.order_no}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Card variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Changed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusChanges.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No status changes recorded for this order.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {[...statusChanges].reverse().map((change, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{formatDateDisplay(change.date)}</TableCell>
                    <TableCell>{change.time}</TableCell>
                    <TableCell>
                      {change.from_status ? (
                        <OrderStatusChip status={change.from_status} />
                      ) : (
                        <Chip label="Created" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <OrderStatusChip status={change.to_status} />
                    </TableCell>
                    <TableCell>{change.changed_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ProjectJobDetailPage;
